// features/partners/page/partner-upsert/partner-upsert.ts
import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

import { TranslatePipe } from '@ngx-translate/core';

import { AppRoutes } from '@core/constants/app.routes';
import { Partner, PartnerUpdate } from '@core/entities/models/partner.model';
import { PartnerService } from '@core/services/use-cases/partner.service';
import { BreadcrumbService } from '@shared/services/breadcrumb.service';
import { SnackbarService } from '@shared/services/snackbar.service';

import { UiMoleculeInputComponent } from '@shared/components/molecules/ui-molecule-input/ui-molecule-input.component';
import { UiAtomButtonComponent } from '@shared/components/atoms/ui-atom-button/ui-atom-button.component';
import { UiAtomSpacerComponent } from '@shared/components/atoms/ui-atom-spacer/ui-atom-spacer.component';
import { UiAtomTextComponent } from '@shared/components/atoms/ui-atom-text/ui-atom-text.component';
import { UiOrganismMainNavComponent } from '@shared/components/organisms/ui-organism-main-nav/ui-organism-main-nav.component';

@Component({
  selector: 'app-partner-upsert',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslatePipe,
    UiMoleculeInputComponent,
    UiAtomButtonComponent,
    UiAtomSpacerComponent,
    UiAtomTextComponent,
    UiOrganismMainNavComponent,
  ],
  templateUrl: './partner-upsert.html',
  styleUrl: './partner-upsert.scss',
})
export class PartnerUpsert {
  private readonly fb = inject(FormBuilder);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly partnerService = inject(PartnerService);
  private readonly breadcrumbService = inject(BreadcrumbService);
  private readonly snackbar = inject(SnackbarService);

  protected readonly AppRoutes = AppRoutes;

  protected readonly form: FormGroup;

  protected editingId: string | null = null;
  protected currentPartner: Partner | null = null;
  protected isLoading = false;

  /** CountryId obtenido de la URL */
  protected countryId: string | null = null;

  constructor() {
    this.form = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(1), Validators.maxLength(50)]],
    });

    // Obtener countryId de la URL (recorrer toda la jerarquía de rutas)
    this.countryId = this.getRouteParam('countryId');

    const idFromRoute = this.route.snapshot.paramMap.get('id');
    if (idFromRoute) {
      this.loadForEdit(idFromRoute);
    }
  }

  /** Obtiene un parámetro de la ruta recorriendo toda la jerarquía */
  private getRouteParam(param: string): string | null {
    let route: ActivatedRoute | null = this.route;
    while (route) {
      const value = route.snapshot.paramMap.get(param);
      if (value) return value;
      route = route.parent;
    }
    return null;
  }

  protected get nameControl() {
    return this.form.get('name');
  }

  private loadForEdit(id: string): void {
    this.partnerService.getById(id).subscribe({
      next: (partner) => {
        this.editingId = partner.id;
        this.currentPartner = partner;

        this.form.patchValue({
          name: partner.name,
        });
      },
      error: (err) => {
        this.snackbar.notifyHttpError(err);
        this.backToList();
      },
    });
  }

  // ---------------------------------------------------------------------------
  // Submit / navegación
  // ---------------------------------------------------------------------------

  protected onSubmit(): void {
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return;
    }

    const raw = this.form.getRawValue();

    if (this.editingId && this.currentPartner) {
      // Modo edición: usar PartnerUpdate
      const payload: PartnerUpdate = {
        name: raw.name,
      };

      this.partnerService.update(this.editingId, payload).subscribe({
        next: () => {
          // Invalidar caché de breadcrumbs para este partner
          this.breadcrumbService.invalidatePartnerCache(this.editingId!);
          this.backToList();
        },
        error: (err) => this.snackbar.notifyHttpError(err),
      });
    } else if (this.countryId) {
      // Modo creación: usar createInCountry con solo el name
      this.isLoading = true;

      this.partnerService.createInCountry(this.countryId, { name: raw.name }).subscribe({
        next: () => {
          this.isLoading = false;
          this.backToList();
        },
        error: (err) => {
          this.isLoading = false;
          this.snackbar.notifyHttpError(err);
        },
      });
    }
  }

  protected cancel(): void {
    this.backToList();
  }

  private backToList(): void {
    void this.router.navigate([
      '/',
      'admin',
      AppRoutes.countries,
      this.countryId,
      AppRoutes.partners,
    ]);
  }
}
